/**
 * Evaluation Report Page
 *
 * Renders the comprehensive report for a scored evaluation:
 * overall scores, domain/component breakdown with rationales,
 * and improvement recommendations.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { AlertTriangle, ArrowLeft, Printer } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '../../components/common/Button';
import {
  EvaluationReport,
  getEvaluationReport,
} from '../../services/evaluationService';

const LEVEL_STYLES: Record<string, string> = {
  highly_effective: 'bg-green-100 text-green-800',
  effective: 'bg-blue-100 text-blue-800',
  developing: 'bg-orange-100 text-orange-800',
  ineffective: 'bg-red-100 text-red-800',
};

const formatLevel = (level: string | null) =>
  level ? level.replace(/_/g, ' ') : '—';

const LevelBadge: React.FC<{ level: string | null }> = ({ level }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
      (level && LEVEL_STYLES[level]) || 'bg-gray-100 text-gray-600'
    }`}
  >
    {formatLevel(level)}
  </span>
);

export const EvaluationReportPage: React.FC = () => {
  const { id: evaluationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<EvaluationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    if (!evaluationId) {
      setError('Evaluation ID is missing');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getEvaluationReport(evaluationId);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [evaluationId]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="py-8 text-center">
        <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          Report Not Available
        </h3>
        <p className="mb-4 text-gray-600">
          {error || 'The report could not be loaded.'}
        </p>
        <Button onClick={() => navigate(`/evaluations/${evaluationId ?? ''}`)}>
          Back to Evaluation
        </Button>
      </div>
    );
  }

  const { scores } = report;
  const recommendations = report.recommendations ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between print:hidden">
        <Button
          variant="secondary"
          onClick={() => navigate(`/evaluations/${report.evaluation_id}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Evaluation
        </Button>
        <Button variant="secondary" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Print / Save PDF
        </Button>
      </div>

      {/* Report header */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Evaluation Report
            </h1>
            <p className="mt-1 text-gray-600">
              {report.rubric?.name ?? 'Rubric'}
              {report.evaluation_period && ` · ${report.evaluation_period}`}
            </p>
          </div>
          <LevelBadge level={scores.performance_level} />
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-sm text-gray-500">Teacher</dt>
            <dd className="font-medium text-gray-900">
              {report.teacher?.name ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Evaluator</dt>
            <dd className="font-medium text-gray-900">
              {report.evaluator?.name ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Overall Score</dt>
            <dd className="font-medium text-gray-900">
              {scores.total_score !== null && scores.max_score !== null
                ? `${scores.total_score.toFixed(2)} / ${scores.max_score.toFixed(0)}`
                : '—'}
              {scores.percentage !== null && (
                <span className="ml-2 text-sm text-gray-500">
                  ({scores.percentage.toFixed(1)}%)
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Scored At</dt>
            <dd className="font-medium text-gray-900">
              {report.scored_at
                ? new Date(report.scored_at).toLocaleString()
                : '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Domain breakdown */}
      {report.domains.map(domain => (
        <div
          key={domain.domain_id}
          className="rounded-lg border bg-white p-6 shadow-sm"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {domain.name ?? 'Domain'}
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                Domain score: {domain.score.toFixed(2)}
              </span>
              <LevelBadge level={domain.performance_level} />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Component</th>
                  <th className="py-2 pr-4">Score</th>
                  <th className="py-2 pr-4">Level</th>
                  <th className="py-2">Rationale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {domain.components.map(component => (
                  <tr key={component.component_id}>
                    <td className="py-2 pr-4 font-medium text-gray-900">
                      {component.code && (
                        <span className="mr-1 text-gray-500">
                          {component.code}
                        </span>
                      )}
                      {component.name ?? component.component_id}
                    </td>
                    <td className="py-2 pr-4">{component.score.toFixed(1)}</td>
                    <td className="py-2 pr-4">
                      <LevelBadge level={component.performance_level} />
                    </td>
                    <td className="py-2 text-gray-600">
                      {component.rationale ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Improvement Recommendations
          </h2>
          <div className="space-y-4">
            {recommendations.map(rec => (
              <div
                key={rec.component_id}
                className="rounded-md border border-gray-200 p-4"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">
                    {rec.component_code} {rec.component_name ?? ''}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      rec.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {rec.priority} priority
                  </span>
                </div>
                <p className="mb-2 text-sm text-gray-600">
                  Current score {rec.current_score.toFixed(1)} → target{' '}
                  {rec.target_score.toFixed(1)}
                </p>
                <ul className="list-inside list-disc text-sm text-gray-600">
                  {rec.suggested_actions.map(action => (
                    <li key={action}>{action}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {(report.evaluator_notes || report.teacher_reflection) && (
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Notes</h2>
          {report.evaluator_notes && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700">
                Evaluator Notes
              </h3>
              <p className="text-sm text-gray-600">{report.evaluator_notes}</p>
            </div>
          )}
          {report.teacher_reflection && (
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                Teacher Reflection
              </h3>
              <p className="text-sm text-gray-600">
                {report.teacher_reflection}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EvaluationReportPage;
